import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

// Account email comes from the signed-in session; changing it is confirmed through a link sent to the new address.
export function AccountEmail() {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [open, setOpen] = useState(false);
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/auth/email-change").then((r) => setPending(r.data.pending_email)).catch(() => {}); }, [user?.email]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/email-change", { new_email: v.trim() });
      setPending(r.data.pending_email); setOpen(false); setV("");
      toast.success(`Verification link sent to ${r.data.pending_email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send verification link"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="account-email" className="mb-4 pb-4 border-b border-stone-100">
      <Label className="text-sm">Current Account Email</Label>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <Input data-testid="account-email-current" readOnly value={user?.email || ""} className="h-11 flex-1 min-w-[200px] bg-stone-50 text-base sm:text-sm" />
        {!open && <Button data-testid="change-email-button" variant="outline" onClick={() => setOpen(true)} className="h-11 rounded-full"><Mail className="h-4 w-4 mr-1.5" />Change Email</Button>}
      </div>
      {pending && <p data-testid="account-email-pending" className="mt-2 text-xs text-amber-800">Waiting for verification of <strong className="break-all">{pending}</strong>. Your current email stays active until you click the link we sent.</p>}
      {open && (
        <div data-testid="change-email-form" className="mt-3 space-y-2">
          <Label htmlFor="new-email" className="text-sm">New email address</Label>
          <Input id="new-email" data-testid="change-email-input" type="email" inputMode="email" autoComplete="email" value={v} onChange={(e) => setV(e.target.value)} className="h-11 text-base sm:text-sm" />
          <div className="flex gap-2">
            <Button variant="outline" data-testid="change-email-cancel" onClick={() => { setOpen(false); setV(""); }} className="h-11 rounded-full flex-1">Cancel</Button>
            <Button data-testid="change-email-submit" disabled={busy || !EMAIL_RE.test(v.trim())} onClick={submit} className="h-11 rounded-full flex-1 bg-amber-800 hover:bg-amber-900 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Verification Link"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
